import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Dark mode global context. Estrategia:
 *  - Persistido en localStorage ("gas2go:theme" = "light"|"dark").
 *  - Se aplica como `class="dark"` en el <html> para que Tailwind
 *    (darkMode: "class") y los overrides en `index.css` tomen
 *    efecto automáticamente en TODA la app sin tocar cada componente.
 *  - Si el cliente no ha elegido, respetamos `prefers-color-scheme`
 *    del sistema (gusto OS-level).
 */
const ThemeContext = createContext({ theme: "light", toggle: () => {}, setTheme: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("gas2go:theme");
      if (saved === "dark" || saved === "light") return saved;
    } catch (_e) { /* ignore */ }
    // Default intencional: modo CLARO para usuarios nuevos. Respetar
    // `prefers-color-scheme` del sistema resultaba en experiencias
    // inconsistentes (usuarios con macOS/Windows en dark por defecto
    // veían la app oscura sin haberlo elegido). El toggle en el menú
    // de usuario permite cambiar a dark en cualquier momento y la
    // selección queda persistida en localStorage.
    return "light";
  });

  useEffect(() => {
    const html = document.documentElement;
    if (theme === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
    try { localStorage.setItem("gas2go:theme", theme); } catch (_e) { /* ignore */ }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
